import { IFileResponse } from '@api/client';
import { isImageByExtension, isVideoByExtension } from '@utils/media-file.helpers';

type VideoThumbnailUrlOptions = {
  previewUrl?: string | null;
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeUrl(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isSuitableThumbnail(url: string): boolean {
  return !isVideoByExtension(url);
}

function stripQueryAndHash(url: string): string {
  const hashIndex = url.indexOf('#');
  const queryIndex = url.indexOf('?');
  const cutIndex = [hashIndex, queryIndex]
    .filter((index) => index >= 0)
    .sort((a, b) => a - b)[0];
  if (cutIndex === undefined) {
    return url;
  }
  return url.slice(0, cutIndex);
}

function buildPreviewUrlFromVideoUrl(videoUrl: string, s3name: string | null): string | null {
  const cleanUrl = stripQueryAndHash(videoUrl);
  if (!cleanUrl) {
    return null;
  }

  const normalizedS3Name = s3name ? s3name.replace(/^\/+/, '') : null;
  if (normalizedS3Name) {
    const encodedS3Name = encodeURI(normalizedS3Name);
    const rawTail = new RegExp(`${escapeRegExp(normalizedS3Name)}$`);
    const encodedTail = new RegExp(`${escapeRegExp(encodedS3Name)}$`);

    if (rawTail.test(cleanUrl)) {
      return cleanUrl.replace(rawTail, `video_preview/${normalizedS3Name}.jpg`);
    }

    if (encodedTail.test(cleanUrl)) {
      return cleanUrl.replace(encodedTail, `video_preview/${encodedS3Name}.jpg`);
    }
  }

  if (cleanUrl.includes('/user_files/')) {
    return `${cleanUrl.replace('/user_files/', '/video_preview/user_files/')}.jpg`;
  }

  return null;
}

export function getVideoThumbnailUrl(
  file: IFileResponse | null | undefined,
  options: VideoThumbnailUrlOptions = {}
): string | null {
  const externalPreview = normalizeUrl(options.previewUrl);
  if (externalPreview && isSuitableThumbnail(externalPreview)) {
    return externalPreview;
  }

  if (!file) {
    return null;
  }

  const embeddedPreview = normalizeUrl(file['preview_url']);
  if (embeddedPreview && isSuitableThumbnail(embeddedPreview)) {
    return embeddedPreview;
  }

  if (Array.isArray(file.resizes)) {
    for (const resize of file.resizes) {
      const resizeUrl = normalizeUrl(resize?.url);
      if (resizeUrl && isSuitableThumbnail(resizeUrl)) {
        return resizeUrl;
      }
    }
  }

  const originUrl = normalizeUrl(file.origin?.url);
  if (originUrl && isImageByExtension(originUrl)) {
    return originUrl;
  }

  const fileUrl = normalizeUrl(file.url);
  if (fileUrl && isImageByExtension(fileUrl)) {
    return fileUrl;
  }

  const previewFromUrl = fileUrl
    ? buildPreviewUrlFromVideoUrl(fileUrl, normalizeUrl((file as Record<string, unknown>)['s3name']))
    : null;
  if (previewFromUrl && isSuitableThumbnail(previewFromUrl)) {
    return previewFromUrl;
  }

  return null;
}
